/**
 * @description 管理加载功能，包括gltf模型、贴图加载和天空盒加载
 * @author TKB
 * @creat 2023-03-06 11:17:24
 */
import {
  TextureLoader,
  PMREMGenerator,
  FloatType,
  LoadingManager,
  LoaderUtils,
  EquirectangularReflectionMapping,
  SRGBColorSpace,
} from "three";
//import * as THREE from "three"; // 从r140版本引入sRGBEncoding等一些常量需要用 THREE.sRGBEncoding，无法使用import方式
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { ZipLoader } from "../lib/ziploader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { BaseManager } from "./BaseManager.js";
import { EventManagerCommand } from "./EventManager.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import Util from "../utils/utils.js";

export class LoadManager extends BaseManager {
  constructor(manager) {
    super(manager);
  }

  init(manager) {
    super.init(manager);
    //gltf
    this.gltfLoadingManager = new LoadingManager();

    //texture
    this.textureMap = new Map();
    this.skyboxMap = new Map();
    this.textureLoader = new TextureLoader();
    this.createPmremGenerator();

    // 执行加载管理器初始化完成的事件。使用settimeout函数是要让这一步的执行异步，也就是先返回这个init函数，让manager拥有loadManager对象，不然会报错不存在loadManager对象
    setTimeout(() => {
      if (this.manager.eventManager) {
        this.manager.eventManager.dispach(
          EventManagerCommand.loadManagerInitCompleted
        );
      }
    }, 0);
  }

  update(dt) {
    super.update(dt);
  }

  clear() {
    this.gltfLoadingManager = null;

    if (this.textureMap) {
      this.textureMap.forEach((tex) => {
        tex.dispose();
      });
      this.textureMap.clear();
    }
    if (this.skyboxMap) {
      this.skyboxMap.forEach((tex) => {
        tex.dispose();
      });
      this.skyboxMap.clear();
    }
    this.pmremGenerator.dispose();

    super.clear();
  }

  createPmremGenerator() {
    if (!this.pmremGenerator) {
      const renderer = this.manager.sceneManager.renderer;
      this.pmremGenerator = new PMREMGenerator(renderer);
      this.pmremGenerator.compileEquirectangularShader();
    }
  }

  /**
   * 加载单个gltf模型
   * @param {String} fileUrl gltf模型路径
   * @param {Function} loadedCb 加载完单个模型后的回调函数，只有一个参数：加载出来的gltf对象
   * @param {Function} progressCb 加载进度的回调函数，只有一个参数：进度值
   * @param {Function} progressiveLoadCb 实现渐进式加载的回调，及加载一个节点显示一个节点。通常只是  scene.add(obj) ，只有一个参数：加载出来的模型
   * @param {Object} extraInfo 额外需要使用的信息，默认为null
   * @returns
   */
  loadScene(url, loadedCb, progressCb) {
    return new Promise((resolve, reject) => {
      const manager = new LoadingManager();

      manager.onProgress = function (url, itemsLoaded, itemsTotal) {
        //console.log( 'Loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.' );
      };

      var self = this;

      function load(configJson) {
        const loader = new GLTFLoader(manager);
        loader.setCrossOrigin("anonymous");

        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath("assets/draco/");
        loader.setDRACOLoader(dracoLoader);
        loader.setMeshoptDecoder(MeshoptDecoder);

        loader.load(
          configJson.fileUrl,
          (gltf) => {
            if (loadedCb) loadedCb(gltf);
            resolve(gltf);
          },
          function (xhr) {
            let process = (xhr.loaded / xhr.total) * 100;
            process =
              process / self.totalModelCount +
              self.curLoadModelIndex * (100 / self.totalModelCount);
            process = process.toFixed(0.1);
            if (process >= 99) process = 99;
            if (progressCb) progressCb(process);
          },
          (error) => {
            console.error("模型加载失败:", error);
            //reject(new Error(`文件加载失败: ${error.message}`));
            resolve(null);
          }
        );
      }

      if (url.toLowerCase().endsWith(".zip")) {
        new Promise((resolve, reject) => {
          if (url.match(/\.zip$/)) {
            new ZipLoader()
              .load(url, (xhr) => {
                let process = (xhr.loaded / xhr.total) * 100;
                process =
                  process / self.totalModelCount +
                  self.curLoadModelIndex * (100 / self.totalModelCount);
                process = process.toFixed(0.1);
                if (process >= 99) process = 99;
                if (progressCb) progressCb(process);
              })
              .then(function (zip) {
                manager.setURLModifier(zip.urlResolver);
                resolve({
                  fileUrl: zip.find(/\.(gltf|glb)$/i)[0],
                });
              });
          } else {
            resolve(url);
          }
        }).then(function (configJson) {
          load({
            fileUrl: configJson.fileUrl,
          });
        });
      } else {
        load({ fileUrl: url });
      }
    });
  }

  /**
   * 按序加载一组gltf模型
   * @param {Array} urls gltf模型路径数组，支持多个
   * @param {Function} loadedCb 加载完单个模型后的回调函数，只有一个参数：加载出来的gltf对象
   * @param {Function} loadedAllCb 全部加载完成后的回调函数，一个参数：数组，加载的全部gltf模型
   * @param {Function} progressCb 加载进度的回调函数，只有一个参数：进度值
   */
  async loadGltfs(urls, loadedCb, loadedAllCb, progressCb) {
    if (!Array.isArray(urls)) {
      urls = [urls];
    }

    this.totalModelCount = urls.length;
    let nowLoadModelIndex=0;
    this.curLoadModelIndex = 0;

    var gltfs = [];

    for (var i = 0; i < urls.length; i++) {
      let url = urls[i];

      const gltf = await this.loadScene(url, loadedCb, progressCb);
      if(gltf) gltfs.push(gltf);

      nowLoadModelIndex++;
      this.curLoadModelIndex++;
      if (nowLoadModelIndex == this.totalModelCount) {
        if (loadedAllCb) loadedAllCb(gltfs);
      }
    }
  }

  /**
   * 异步加载图片，调用需要使用await
   * @param {String} path 图片路径
   * @param {function(tex)} cb 带有一个参数的函数，加载完贴图会被后调用
   * @returns 返回贴图
   */
  loadTexture(path, cb) {
    return new Promise((resolve, reject) => {
      if (this.textureMap.has(path)) {
        let tex = this.textureMap.get(path);
        if (cb) {
          cb(tex);
        }
        resolve(tex);
      } else {
        if (path.indexOf(".hdr") >= 0) {
          new RGBELoader().setDataType(FloatType).load(
            path,
            (tex) => {
              this.textureMap.set(path, tex);
              if (cb) cb(tex);
              resolve(tex);
            },
            null,
            (e) => {
              reject(e);
            }
          );
        } else {
          this.textureLoader.load(
            path,
            (tex) => {
              this.textureMap.set(path, tex);
              if (!Util.getOS().isPc) tex.colorSpace = SRGBColorSpace;
              if (cb) cb(tex);
              resolve(tex);
            },
            null,
            (e) => {
              reject(e);
            }
          );
        }
      }
    });
  }

  /**
   * 异步获取天空盒，调用需要使用await
   * @param {String} path 路径
   * @param {function(tex)} cb 带有一个参数的函数，加载完贴图会被后调用
   * @returns
   */
  getCubeMapTexture(path, cb) {
    if (!path) return;
    let scope = this;
    return new Promise((resolve, reject) => {
      if (this.skyboxMap.has(path)) {
        const tex = this.skyboxMap.get(path);
        if (cb) cb(tex);
        resolve(tex);
      }
      if (path.indexOf(".hdr") >= 0) {
        new RGBELoader().load(
          path,
          (texture) => {
            texture.mapping = EquirectangularReflectionMapping;
            if (!scope.pmremGenerator) scope.createPmremGenerator();
            scope.pmremGenerator.compileEquirectangularShader();
            const envMap =
              scope.pmremGenerator.fromEquirectangular(texture).texture;
            scope.pmremGenerator.dispose();
            this.skyboxMap.set(path, envMap);
            if (cb) cb(envMap);
            resolve(envMap);
          },
          undefined,
          reject
        );
      } else {
        this.textureLoader.load(
          path,
          (texture) => {
            if (!scope.pmremGenerator) scope.createPmremGenerator();
            const envMap =
              this.pmremGenerator.fromEquirectangular(texture).texture;
            this.pmremGenerator.dispose();
            this.skyboxMap.set(path, envMap);
            if (cb) cb(envMap);
            resolve(envMap);
          },
          undefined,
          reject
        );
      }
    });
  }
}
